import { useState } from 'react'
import { BAND, GOLD, GRID, INK, INK_MID } from './chartColors.js'
import { domainOf, linear, round, segmentsOf, ticksOf } from './scales.js'

export interface LineSeries {
  key: string
  name: string
  color: string
  values: readonly (number | null)[]
  dashed?: boolean
  /** A dash pattern of the series' own, so it is told apart beside its colour. */
  dash?: string | undefined
}

export interface ChartMarker {
  /** The index of the month the rule stands at. */
  index: number
  label: string
}

interface LineChartProps {
  periods: readonly string[]
  series: readonly LineSeries[]
  /** A shaded band between two series, drawn behind the lines. */
  band?: { low: readonly (number | null)[]; high: readonly (number | null)[]; name: string } | null
  markers?: readonly ChartMarker[]
  domain?: [number, number]
  format: (value: number) => string
  periodLabel: (period: string) => string
  title: string
  height?: number
  /** The drawing's width in its own units: 1200 for a full-width chart, about 420 for a card a third as wide, so the text stays readable. */
  width?: number
}

const PAD = { top: 16, right: 20, bottom: 24, left: 44 }
const TOOLTIP_WIDTH = 210
const LINE = 18

/**
 * A line chart over months: a band behind, the series over it, a dotted gold rule where a setting changed, and a
 * crosshair with every series' value for the month under the pointer. Plain SVG, so it prints as it shows.
 */
export function LineChart({ periods, series, band = null, markers = [], domain, format, periodLabel, title, height = 300, width = 1200 }: LineChartProps) {
  const [hover, setHover] = useState<number | null>(null)
  const WIDTH = width
  const plotWidth = WIDTH - PAD.left - PAD.right
  const plotHeight = height - PAD.top - PAD.bottom
  const every = [...series.flatMap((entry) => entry.values), ...(band ? [...band.low, ...band.high] : [])]
  const [min, max] = domain ?? domainOf(every, [0, 100])
  const x = (index: number): number => PAD.left + (periods.length <= 1 ? plotWidth / 2 : (index / (periods.length - 1)) * plotWidth)
  const y = linear(min, max, PAD.top + plotHeight, PAD.top)
  const bandPath = band ? bandPathOf(band.low, band.high, x, y) : null
  const hovered = hover === null ? null : periods[hover]
  const tooltipLeft = hover !== null && x(hover) > WIDTH * 0.6
  const latest = series.map((entry) => {
    const lastIndex = entry.values.reduce<number>((found, value, index) => (value === null ? found : index), -1)
    const value = lastIndex === -1 ? null : entry.values[lastIndex]
    return `${entry.name} ${value === null || value === undefined ? 'nothing' : format(value)}${lastIndex === -1 ? '' : ` in ${periodLabel(periods[lastIndex] ?? '')}`}`
  })

  const track = (event: React.PointerEvent<SVGSVGElement>): void => {
    const box = event.currentTarget.getBoundingClientRect()
    const ratio = (event.clientX - box.left) / box.width
    const px = ratio * WIDTH
    const index = Math.round(((px - PAD.left) / plotWidth) * (periods.length - 1))
    setHover(index < 0 || index >= periods.length ? null : index)
  }

  return (
    <svg role="img" aria-label={title} viewBox={`0 0 ${WIDTH} ${height}`} className="block w-full" onPointerMove={track} onPointerLeave={() => setHover(null)}>
      <desc>
        {title}. Latest: {latest.join('; ')}.{markers.length > 0 ? ` ${markers.length} setting ${markers.length === 1 ? 'change' : 'changes'} marked.` : ''}
      </desc>
      {ticksOf([min, max]).map((tick) => (
        <g key={tick}>
          <line x1={PAD.left} y1={round(y(tick))} x2={WIDTH - PAD.right} y2={round(y(tick))} stroke={GRID} strokeWidth={1} />
          <text x={PAD.left - 8} y={round(y(tick)) + 4} textAnchor="end" fontSize={11} fill={INK_MID}>
            {format(tick)}
          </text>
        </g>
      ))}
      {bandPath && <path d={bandPath} fill={BAND} opacity={0.8} />}
      {markers.map((marker, index) => (
        <line key={`${marker.index}-${index}`} x1={round(x(marker.index))} y1={PAD.top} x2={round(x(marker.index))} y2={PAD.top + plotHeight} stroke={GOLD} strokeWidth={2} strokeDasharray="2 4">
          <title>{marker.label}</title>
        </line>
      ))}
      {series.map((entry) =>
        segmentsOf(entry.values, x, y).map((points, index) => (
          <polyline key={`${entry.key}-${index}`} points={points} fill="none" stroke={entry.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" strokeDasharray={entry.dashed ? '6 4' : entry.dash} />
        )),
      )}
      {series.map((entry) => {
        const lastIndex = entry.values.reduce<number>((found, value, index) => (value === null ? found : index), -1)
        const last = lastIndex === -1 ? null : entry.values[lastIndex]
        return last === null || last === undefined ? null : <circle key={`${entry.key}-end`} cx={round(x(lastIndex))} cy={round(y(last))} r={4} fill={entry.color} stroke="#fff" strokeWidth={2} />
      })}
      {periods.map((period, index) =>
        periods.length <= 13 || index % Math.ceil(periods.length / 12) === 0 ? (
          <text key={period} x={round(x(index))} y={height - 6} textAnchor="middle" fontSize={11} fill={INK_MID}>
            {periodLabel(period)}
          </text>
        ) : null,
      )}
      {hover !== null && hovered && (
        <g>
          <line x1={round(x(hover))} y1={PAD.top} x2={round(x(hover))} y2={PAD.top + plotHeight} stroke={INK_MID} strokeWidth={1} strokeDasharray="3 3" />
          {series.map((entry) => {
            const value = entry.values[hover]
            return value === null || value === undefined ? null : <circle key={entry.key} cx={round(x(hover))} cy={round(y(value))} r={5} fill="#fff" stroke={entry.color} strokeWidth={2} />
          })}
          <foreignObject x={tooltipLeft ? round(x(hover)) - TOOLTIP_WIDTH - 12 : round(x(hover)) + 12} y={PAD.top} width={TOOLTIP_WIDTH} height={12 + LINE * (series.length + 1 + (band ? 1 : 0))} style={{ overflow: 'visible' }}>
            <div style={{ background: INK, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 11, lineHeight: `${LINE}px`, whiteSpace: 'nowrap' }}>
              <div style={{ color: '#B8D4DC' }}>{periodLabel(hovered)}</div>
              {series.map((entry) => {
                const value = entry.values[hover]
                return (
                  <div key={entry.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 10, height: 2, background: entry.color, display: 'inline-block' }} />
                      {entry.name}
                    </span>
                    <strong>{value === null || value === undefined ? '—' : format(value)}</strong>
                  </div>
                )
              })}
              {band && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, color: '#B8D4DC' }}>
                  <span>{band.name}</span>
                  <span>
                    {formatOrDash(band.low[hover], format)} to {formatOrDash(band.high[hover], format)}
                  </span>
                </div>
              )}
            </div>
          </foreignObject>
        </g>
      )}
    </svg>
  )
}

function formatOrDash(value: number | null | undefined, format: (value: number) => string): string {
  return value === null || value === undefined ? '—' : format(value)
}

/** The band's outline: along the high series, back along the low one, over the months both have. */
function bandPathOf(low: readonly (number | null)[], high: readonly (number | null)[], x: (index: number) => number, y: (value: number) => number): string | null {
  const indexes = low.map((_, index) => index).filter((index) => low[index] !== null && high[index] !== null)
  if (indexes.length < 2) {
    return null
  }
  const top = indexes.map((index) => `${round(x(index))} ${round(y(high[index] as number))}`)
  const bottom = [...indexes].reverse().map((index) => `${round(x(index))} ${round(y(low[index] as number))}`)
  return `M${top.join(' L')} L${bottom.join(' L')} Z`
}
